import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Renglon, totalRenglones, fmtDuracion, plata } from '../../utils/turnoNuevo';

interface Props {
  cliente: { titulo: string; sub?: string } | null;
  vehiculo: { titulo: string; sub?: string } | null;
  cuando: { titulo: string; sub?: string; tono: 'ok' | 'info' | 'muted'; badge: string };
  presupuesto: string | null;
  renglones: Renglon[];
  duracion: number;
  guardando: boolean;
  ctaLabel: string;
  nota: string;
  onCrear: () => void;
  onCancelar: () => void;
}

const MAX_ITEMS = 6;

const Dato: React.FC<{ label: string; valor?: string | null; sub?: string }> = ({ label, valor, sub }) => (
  <>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="min-w-0">
      {valor ? (
        <>
          <div className="truncate font-medium">{valor}</div>
          {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
        </>
      ) : (
        <span className="italic text-muted-foreground/70">todavía no</span>
      )}
    </dd>
  </>
);

/** Resumen fijo a la derecha (en el teléfono, una banda abajo) con el botón de crear */
const ResumenTurno: React.FC<Props> = ({ cliente, vehiculo, cuando, presupuesto, renglones, duracion, guardando, ctaLabel, nota, onCrear, onCancelar }) => {
  const total = totalRenglones(renglones);
  const badgeCls = cuando.tono === 'ok'
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : cuando.tono === 'info'
      ? 'bg-primary/10 text-primary'
      : 'bg-muted text-muted-foreground';

  return (
    <>
      <aside className="overflow-hidden rounded-xl border border-border bg-card shadow-sm lg:sticky lg:top-4" aria-label="Resumen del turno">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Resumen</h3>
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', badgeCls)}>{cuando.badge}</span>
        </div>
        <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-sm">
          <Dato label="Cliente" valor={cliente?.titulo} sub={cliente?.sub} />
          <Dato label="Vehículo" valor={vehiculo?.titulo} sub={vehiculo?.sub} />
          <Dato label="Cuándo" valor={cuando.titulo || null} sub={cuando.sub} />
          <dt className="text-muted-foreground">Presupuesto</dt>
          <dd className="min-w-0 truncate">{presupuesto ? <span className="font-medium">{presupuesto}</span> : <span className="italic text-muted-foreground/70">ninguno</span>}</dd>
        </dl>
        <div className="border-t border-border px-4 py-3 text-sm">
          {renglones.length === 0 ? (
            <div className="italic text-muted-foreground/70">Sin servicios ni productos</div>
          ) : (
            <>
              {renglones.slice(0, MAX_ITEMS).map((r, i) => (
                <div key={`${r.type}-${r.id}-${i}`} className="flex items-baseline justify-between gap-3 py-0.5">
                  <span className="min-w-0 truncate">{r.qty}× {r.name}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{plata(r.sellingPrice * r.qty)}</span>
                </div>
              ))}
              {renglones.length > MAX_ITEMS && (
                <div className="py-0.5 text-xs text-muted-foreground">+{renglones.length - MAX_ITEMS} más</div>
              )}
            </>
          )}
          <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2 font-semibold tabular-nums">
            <span>Total estimado</span>
            <span>{plata(total)}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">Duración estimada {fmtDuracion(duracion)}</div>
        </div>
        <div className="hidden flex-col gap-2 border-t border-border px-4 py-3 lg:flex">
          <Button type="button" className="h-10 w-full" onClick={onCrear} disabled={guardando}>
            {guardando ? 'Guardando…' : ctaLabel}
          </Button>
          {nota && <p className="text-center text-xs text-muted-foreground">{nota}</p>}
          <Button type="button" variant="outline" className="h-9 w-full" onClick={onCancelar} disabled={guardando}>Cancelar</Button>
        </div>
      </aside>

      {/* Banda inferior en el teléfono */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-border bg-card px-4 py-2.5 shadow-[0_-8px_24px_-16px_rgba(0,0,0,.5)] lg:hidden">
        <div className="min-w-0 flex-1 text-xs text-muted-foreground">
          <div className="truncate text-sm font-semibold text-foreground tabular-nums">{plata(total)} · {fmtDuracion(duracion)}</div>
          <div className="truncate">{cuando.titulo || 'Falta elegir cuándo'}</div>
        </div>
        <Button type="button" className="h-10 shrink-0" onClick={onCrear} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Crear turno'}
        </Button>
      </div>
    </>
  );
};

export default ResumenTurno;
